import { useToken } from '@/app/hooks';
import {
  CategoryType,
  Mission,
  useMissionDetail,
  useTradeMission,
} from '@/app/hooks/trade/tradeMission';
import { Column } from '@/styles/reactnative';
import { ThemeContext } from '@/theme/ThemeProvider';
import BigNumber from 'bignumber.js';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import tether from '../../assets/tether.png';
import RewardButton from '../../RewardButton';
import { DisabledRewardButton } from '../../components/DisabledRewardButton';
import {
  canClaimable,
  getClaimStatus,
  getMissionTemplateCode,
  MissionCode,
} from '../../utils';
import * as S from './styles';
import useNavigationHandler from '../../useNavigationHandler';
import RewardLoading from '../RewardLoading';
import { useMyProfile } from '@/app/hooks/user/myprofile';

interface DetailMissions {
  register: Mission[];
  firstDeposit: Mission[];
}

const NewUser = () => {
  const [detailMissions, setDetailMissions] = useState<DetailMissions>({
    register: [],
    firstDeposit: [],
  });
  const [isShowRegister, setIsShowRegister] = useState(false);
  const [isShowFirstDeposit, setIsShowFirstDeposit] = useState(false);

  const { data, getMissionReward, isLoading } = useTradeMission(
    CategoryType.ONBOARDING
  ); // 카테고리별 API 호출
  const { isDarkMode, theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useToken();
  const { data: myProfile } = useMyProfile();
  const onboardingMissions = data?.missions ?? []; // 이미 필터링된 데이터
  const handleNavigation = useNavigationHandler();

  // 첫 입금 미션 상세 조회 (useSWR로 3초 캐싱)
  const firstDepositMissionId = detailMissions.firstDeposit[0]?.id;
  const { data: detailFirstDepositMission } = useMissionDetail(
    firstDepositMissionId
  );

  const registerMissions = isShowRegister
    ? detailMissions.register
    : detailMissions.register.slice(0, 2);

  const firstDepositMissions = isShowFirstDeposit
    ? (detailFirstDepositMission?.conditions?.tiers ?? [])
    : (detailFirstDepositMission?.conditions?.tiers ?? []).slice(0, 2);

  // 첫 입금 미션 유효기간 계산
  const calculateRemainingDays = () => {
    if (
      !myProfile?.created_at ||
      !detailFirstDepositMission?.conditions?.validWithinDaysFromSignup
    ) {
      return null;
    }

    const signupDate = new Date(myProfile.created_at);
    const validDays =
      detailFirstDepositMission.conditions.validWithinDaysFromSignup;
    const expiryDate = new Date(signupDate);
    expiryDate.setDate(expiryDate.getDate() + validDays);

    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // 날짜 포맷팅 (YYYY-MM-DD)
    const formatDate = (date: Date) => {
      return date.toISOString().split('T')[0];
    };

    return {
      validDays,
      remainingDays: diffDays > 0 ? diffDays : 0,
      isExpired: diffDays <= 0,
      signupDateFormatted: formatDate(signupDate),
    };
  };

  const missionValidity = calculateRemainingDays();

  // 신규 사용자를 위한 미션 중, 가입 보상과 첫 입금 미션을 분리합니다.
  useEffect(() => {
    if (!onboardingMissions || !onboardingMissions.length) return;

    setDetailMissions({
      register: onboardingMissions.filter(
        (mission) =>
          getMissionTemplateCode(mission.code) === MissionCode.SIGNUP_BONUS ||
          getMissionTemplateCode(mission.code) === MissionCode.KYC_COMPLETE
      ),
      firstDeposit: onboardingMissions.filter(
        (mission) =>
          getMissionTemplateCode(mission.code) ===
          MissionCode.FIRST_DEPOSIT_PROMO
      ),
    });
  }, [onboardingMissions]);

  if (isLoading || !registerMissions.length || !firstDepositMissions.length) {
    return <RewardLoading messageKey="loadingNewUserMission" />;
  }

  return (
    <S.SummaryContainer>
      <Column style={{ alignItems: 'center' }}>
        <S.SummaryItem>
          <Column style={{ gap: '26px' }}>
            <S.SummaryItemTitle>{t('Welcome Reward')}</S.SummaryItemTitle>

            <S.SummaryItemContentWrapper>
              {registerMissions.map((mission, index) => {
                const code = getMissionTemplateCode(mission.code);
                console.log("mission", mission);
                return (
                  <S.SummaryItemContent
                    key={mission.id}
                    isDarkmode={isDarkMode}
                  >
                    <S.SummaryItemDes>
                      {t(`mission_name.${code}`, {
                        defaultValue: mission.name,
                      })}
                    </S.SummaryItemDes>
                    <S.SummaryItemName>
                      <S.tetherImg src={tether} alt="tether" />
                      <span>
                        {new BigNumber(mission.rewardAmount).toFormat()} USDT{' '}
                        {t('payOff')}
                      </span>
                    </S.SummaryItemName>

                    <RewardButton
                      missionId={mission.id}
                      canClaimable={canClaimable(mission, index)}
                      getMissionStatus={getClaimStatus(mission, index)}
                      handleClaim={() =>
                        getMissionReward(
                          mission.id,
                          undefined,
                          mission.rewardAmount
                        )
                      }
                      style={
                        canClaimable(mission, index)
                          ? {
                              marginTop: '6px',
                              width: '100%',
                              padding: '10px',
                              backgroundColor: '#FF5C17',
                              color: 'white',
                            }
                          : {
                              marginTop: '6px',
                              width: '100%',
                              padding: '10px',
                              color: 'white',
                            }
                      }
                      notCompletedComponent={
                        <DisabledRewardButton
                          variant="orange"
                          onClick={() => handleNavigation('/settings?tab=2')}
                          style={{ height: '46px'}}
                        >
                          {t('verifyNow')}
                        </DisabledRewardButton>
                      }
                    />
                  </S.SummaryItemContent>
                );
              })}
            </S.SummaryItemContentWrapper>
          </Column>
        </S.SummaryItem>

        {!isShowRegister && detailMissions.register.length > 2 && (
          <S.ExternalTabButton
            isDarkmode={isDarkMode}
            onClick={() => setIsShowRegister(true)}
          >
            <span>{t('more')}</span>
          </S.ExternalTabButton>
        )}
        {isShowRegister && detailMissions.register.length > 2 && (
          <S.ExternalTabButton
            isDarkmode={isDarkMode}
            onClick={() => setIsShowRegister(false)}
          >
            <span>{t('hide')}</span>
          </S.ExternalTabButton>
        )}
      </Column>

      <Column style={{ alignItems: 'center' }}>
        <S.SummaryItem>
          <Column style={{ gap: '26px' }}>
            <S.SummaryItemTitle>
              {t('Limited-time first deposit mission')}
            </S.SummaryItemTitle>

            <S.DescriptionContainer isDarkmode={isDarkMode}>
              <S.DescriptionContent>
                {missionValidity && (
                  <>
                    {missionValidity.isExpired ? (
                      <>{t('firstDepositMissionExpired')}</>
                    ) : (
                      <>
                        {t('firstDepositMissionValidityNotice', {
                          days: missionValidity.validDays,
                        })}
                        <br />
                        {t('firstDepositMissionSignupDate', {
                          date: missionValidity.signupDateFormatted,
                        })}{' '}
                        <span style={{ fontSize: '12px' }}>
                          {t('firstDepositMissionUTCNotice')}
                        </span>
                        <br />
                        <strong>
                          {t('firstDepositMissionDaysRemaining', {
                            days: missionValidity.remainingDays,
                          })}
                        </strong>
                      </>
                    )}
                  </>
                )}
              </S.DescriptionContent>
            </S.DescriptionContainer>
            <S.SummaryItemContentWrapper>
              {firstDepositMissions.map((tier, index) => {
                return (
                  <S.SummaryItemContent key={tier.id} isDarkmode={isDarkMode}>
                    <S.SummaryItemDes>
                      {tier.minAmount} USDT {t('upon deposit')}
                    </S.SummaryItemDes>
                    <S.SummaryItemName>
                      <S.tetherImg src={tether} alt="tether" />
                      <span>
                        {new BigNumber(tier.rewardAmount).toFormat()} USDT{' '}
                        {t('payOff')}
                      </span>
                    </S.SummaryItemName>

                    <RewardButton
                      missionId={detailMissions.firstDeposit[0]!.id}
                      canClaimable={canClaimable(
                        detailMissions.firstDeposit[0]!,
                        index
                      )}
                      getMissionStatus={getClaimStatus(
                        detailMissions.firstDeposit[0]!,
                        index
                      )}
                      handleClaim={() =>
                        getMissionReward(
                          detailMissions.firstDeposit[0]!.id,
                          index,
                          tier.rewardAmount
                        )
                      }
                      style={
                        canClaimable(detailMissions.firstDeposit[0]!, index)
                          ? {
                              marginTop: '6px',
                              width: '100%',
                              padding: '10px',
                              backgroundColor: '#FF5C17',
                              color: 'white',
                            }
                          : {
                              marginTop: '6px',
                              width: '100%',
                              padding: '10px',
                              color: 'white',
                            }
                      }
                      notCompletedComponent={
                        <DisabledRewardButton
                          variant="orange"
                          onClick={() => handleNavigation('/buysell-crypto')}
                        >
                          {t('DepositNow')}
                        </DisabledRewardButton>
                      }
                    />
                  </S.SummaryItemContent>
                );
              })}
            </S.SummaryItemContentWrapper>
          </Column>
        </S.SummaryItem>

        {!isShowFirstDeposit &&
          (detailFirstDepositMission?.conditions?.tiers?.length ?? 0) > 2 && (
            <S.ExternalTabButton
              isDarkmode={isDarkMode}
              onClick={() => setIsShowFirstDeposit(true)}
            >
              <span>{t('more')}</span>
            </S.ExternalTabButton>
          )}
        {isShowFirstDeposit &&
          (detailFirstDepositMission?.conditions?.tiers?.length ?? 0) > 2 && (
            <S.ExternalTabButton
              isDarkmode={isDarkMode}
              onClick={() => setIsShowFirstDeposit(false)}
            >
              <span>{t('hide')}</span>
            </S.ExternalTabButton>
          )}
      </Column>
    </S.SummaryContainer>
  );
};

export default NewUser;
